/**
 * The focus point's ground: the terrain height under the focus camera's
 * anchor, eased onto the finest tile read there so far.
 */

import type { AtlasLive, AtlasState } from '../atlas';
import type { PyramidViewHandle } from './pyramidView.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { SolarScrubHandle } from './solarScrub.ts';
import type { InjectsHandle } from './injects.ts';

export interface FocusGroundDeps {
  readonly focusRig: FocusRigHandle;
  readonly solarScrub: SolarScrubHandle;
  readonly live: AtlasLive;
  readonly state: AtlasState;
  zoomFloorOff(): boolean;
  /** Whether the walkaround's own rig owns the eye, which the focus point's ground does not move. */
  plotCamera(): boolean;
  readonly pyramidView: PyramidViewHandle;
  readonly injects: InjectsHandle;
}

/** The glide's own state that the focus rig and the probe read. */
export interface FocusGroundState {
  readonly focusGroundM: number;
}

export interface FocusGroundHandle {
  readonly GROUND_GLIDE_M_PER_S: 60;
  updateFocusGround(dt: number): void;
  /** Whether this frame's `updateFocusGround` took the eye down, after the frame had placed it. */
  readonly eyeRode: boolean;
  /**
   * The ground height at a focus point: the held reading where it belongs to
   * that point, and the datum anywhere else. Asked for at the focus camera's
   * own anchor after a jump, it acquires that anchor's ground on the spot.
   */
  groundAt(bodyId: string, latDeg: number, lonDeg: number): number;
  readonly state: FocusGroundState;
}

export function createFocusGround(deps: FocusGroundDeps): FocusGroundHandle {
  const { state, zoomFloorOff } = deps;

  /**
   * Terrain height under the focus point, exaggerated as the tiles draw it.
   *
   * Eased toward the finest reading `updateFocusGround` has accepted so far
   * at the current anchor, so the focus lands on the ground the player can
   * see without chasing every intermediate tile a first descent loads.
   * Zero until a leaf covers the point, which is the datum and is always
   * defined.
   */
  let focusGroundM = 0;
  /** What `focusGroundM` eases toward: the last accepted reading, held. */
  let focusGroundWant = 0;
  /** Tile level of the last accepted reading; -1 means none held yet. */
  let focusGroundLevel = -1;
  /** Body and anchor lat/lon the held reading belongs to; null when nothing is held. */
  let focusGroundBody: string | null = null;
  let focusGroundLat: number | null = null;
  let focusGroundLon: number | null = null;
  /** Anchor motion past this many degrees drops the held level and reading. */
  const FOCUS_GROUND_RESET_DEG = 0.05;
  /** Whether the next accepted reading is this anchor's FIRST, and so arrives
   *  rather than eases: see `updateFocusGround`. */
  let focusGroundAcquiring = true;

  /** The rate a ground correction reaches the camera at, metres per second. */
  const GROUND_GLIDE_M_PER_S = 60;
  /** The held ease's cap as a share of the altitude per second. */
  const FOCUS_CLOSE_PER_S = 0.5;
  /** Seconds after a wheel notch in that the eye may ride a lower reading down: longer than the 0.3 to 0.7 s between notches of a steady scroll. */
  const ZOOM_RIDE_S = 1.5;
  /** The most of the altitude one frame's ride moves the eye, under the 5% a frame reads as a jump. */
  const RIDE_FRAME_SHARE = 0.03;
  let zoomInS = 0;
  let lastZoomSteps = 0;
  /** Metres the ground under the eye has come down during this zoom that the eye has not followed yet. */
  let rideRoomM = 0;
  let lastEyeGroundR: number | null = null;
  let eyeRode = false;

  /** `?stalefocusground=1` and `?zoomnofloor=1` both keep the last anchor's
   *  ground under a new one until a leaf at the new one is read. */
  const carriesLastGround = (): boolean => deps.injects.state.STALE_FOCUS_GROUND || zoomFloorOff();

  function holds(bodyId: string, lat: number, lon: number): boolean {
    return focusGroundBody === bodyId && focusGroundLat !== null && focusGroundLon !== null
      && Math.abs(lat - focusGroundLat) <= FOCUS_GROUND_RESET_DEG
      && Math.abs(lon - focusGroundLon) <= FOCUS_GROUND_RESET_DEG;
  }

  /** Take `want` to the finest leaf under the anchor, arriving on a first reading. */
  function readLeaf(lat: number, lon: number): void {
    const hit = deps.pyramidView.tileHeightProbe(lat, lon, false);
    if (!hit || hit.level < focusGroundLevel) return;
    focusGroundLevel = hit.level;
    focusGroundWant = hit.h * deps.solarScrub.solar.exag;
    // A new focus ACQUIRES its ground; it does not glide onto it from
    // wherever the last one sat. The ease below is for a finer leaf
    // arriving under a settled anchor, and at its cap it needs half a
    // minute to climb Tharsis - long enough for a wheel to reach the
    // anchor while the anchor is still a kilometre under the ridge.
    if (focusGroundAcquiring && !zoomFloorOff()) focusGroundM = focusGroundWant;
    focusGroundAcquiring = false;
  }

  /**
   * A new focus. The last anchor's reading is someone else's ground: a jump
   * from Mars's north plains that measured its 400 m from their -4 km put the
   * eye 4 km under the claim's ground, and the eye floor then held it at 7 km.
   */
  function acquire(bodyId: string, lat: number, lon: number): void {
    focusGroundBody = bodyId;
    focusGroundLat = lat;
    focusGroundLon = lon;
    focusGroundLevel = -1;
    focusGroundAcquiring = true;
    if (!carriesLastGround()) {
      focusGroundWant = 0;
      focusGroundM = 0;
    }
    readLeaf(lat, lon);
  }

  /**
   * Ease the focus point onto the terrain.
   *
   * `want` only moves to a reading from a tile at least as fine as the one
   * that set it last, at this same anchor - a coarser tile standing in
   * while a finer one is still loading, or a frame with nothing resident
   * yet, holds instead of pulling the anchor back toward the datum. The
   * held level and reading drop when the anchor itself moves (a new focus,
   * not the zoom-in-place a descent is) or the body changes.
   *
   * Snapped once the remaining offset is under a centimetre, so a held camera
   * comes to rest exactly - `descent-dojo --static` is byte-identical frames,
   * and an exponential that never arrives would keep repainting.
   */
  function updateFocusGround(dt: number): void {
    eyeRode = false;
    const steps = deps.focusRig.focusCam.zoomInSteps;
    zoomInS = steps !== lastZoomSteps ? ZOOM_RIDE_S : Math.max(0, zoomInS - dt);
    lastZoomSteps = steps;
    const g = eyeGroundRadius();
    if (zoomInS === 0) rideRoomM = 0;
    else if (g !== null && lastEyeGroundR !== null) rideRoomM = Math.max(0, rideRoomM + lastEyeGroundR - g);
    easeFocusGround(dt);
    lastEyeGroundR = eyeGroundRadius();
  }

  function easeFocusGround(dt: number): void {
    const lat = deps.focusRig.focusCam.state.latDeg;
    const lon = deps.focusRig.focusCam.state.lonDeg;
    const body = state.bodyId;
    if (lat === null || lon === null || body === null || body !== deps.pyramidView.tileBody) {
      focusGroundBody = null;
      focusGroundLat = null;
      focusGroundLon = null;
      focusGroundLevel = -1;
      focusGroundWant = 0;
      focusGroundAcquiring = true;
    } else if (!holds(body, lat, lon)) {
      acquire(body, lat, lon);
    } else {
      readLeaf(lat, lon);
    }
    const want = focusGroundWant;
    const cam = deps.focusRig.focusCam;
    if (deps.plotCamera()) cam.noteStandEase('stood');
    // The eye's own floor binding says the focus point is under the ground the
    // eye can see. Close the lag in one step instead of gliding there for the
    // half minute per kilometre the cap takes, and take the same metres off the
    // altitude so the EYE does not move with it: what changes is the number the
    // card reads, which was the eye's height over a focus point in the ridge.
    if (!zoomFloorOff() && deps.focusRig.focusCam.groundFloorHeldM > 0.01 && want - focusGroundM > 0.01) {
      const hold = eyeRadius();
      focusGroundM = want;
      deps.focusRig.focusCam.holdEyeRadius(hold);
      deps.live.cameraDirty = true;
      return;
    }
    if (Math.abs(want - focusGroundM) < 0.01) {
      focusGroundM = want;
      cam.noteStandEase('closed');
      return;
    }
    cam.noteStandEase('moving');
    // The anchor's correction rides the same rate as the eye's, so a tile
    // arrival cannot move the two at different speeds. Under 20 m the ease is
    // already slower than the cap and nothing here changes.
    const step = (want - focusGroundM) * (1 - Math.exp(-dt * 3));
    // A goTo or setAltitude into SURFACE asks to stand on this ground and follows its ease
    // to the close, a notch inside it or not: held, 2 m at Jezero stood 2.4 km up in PLOT.
    const standAsked = deps.focusRig.band() === 'SURFACE' && (cam.standEaseOwed || !cam.wheelZoomed);
    // Held, a point settling lower moves the altitude up, so mid zoom in the eye goes down with it instead
    // and the card keeps falling; a point settling higher, or one with no zoom under way, keeps the eye still.
    const ride = !standAsked && !zoomFloorOff() && !deps.plotCamera() && step < 0 && zoomInS > 0
      && !deps.injects.state.ZOOM_DROP_CLIMBS;
    if (ride) {
      // Only as far as the ground under the eye has come down too, else the point waits: at Gale the leaf under the
      // anchor reads 2.2 km lower seconds before the one under the eye, and the eye rode onto ground still drawn high.
      const alt = deps.focusRig.focusCam.state.altitudeM;
      const reach = Math.min(rideRoomM, RIDE_FRAME_SHARE * alt, Math.max(GROUND_GLIDE_M_PER_S, FOCUS_CLOSE_PER_S * alt) * dt);
      const s = Math.max(step, -reach);
      if (s >= 0) return;
      focusGroundM += s;
      rideRoomM += s;
      // The eye's floor stands on ground held over the drawn ground by a 60 m/s glide; at Gale 2 km of it
      // kept the eye on its floor to the end of the zoom. The eye going down takes that much of it along.
      deps.pyramidView.spendGroundGlide(-s);
      // The eye's floor, which a held eye gets from `holdEyeRadius`.
      deps.focusRig.focusCam.reclampAltitude();
      deps.live.cameraDirty = true;
      eyeRode = true;
      return;
    }
    // The eye stays where the wheel left it while the point eases under it: riding
    // the ease lifted it 60 m/s between notches, so a notch in over a deep pivot rose.
    const hold = zoomFloorOff() || deps.plotCamera() || standAsked ? null : eyeRadius();
    // Held, the ease no longer moves the eye's height, so it closes at the view's scale: at 60 m/s
    // a 500 m correction still lagged when the eye reached the ground.
    const cap = (hold === null ? GROUND_GLIDE_M_PER_S
      : Math.max(GROUND_GLIDE_M_PER_S, FOCUS_CLOSE_PER_S * deps.focusRig.focusCam.state.altitudeM)) * dt;
    focusGroundM += Math.max(-cap, Math.min(cap, step));
    if (hold !== null) deps.focusRig.focusCam.holdEyeRadius(hold);
    deps.live.cameraDirty = true;
  }

  /** The drawn ground's radius under the eye, less the correction still gliding in over it; null where none draws. */
  function eyeGroundRadius(): number | null {
    const id = deps.focusRig.focusCam.state.bodyId;
    if (id !== deps.pyramidView.tileBody) return null;
    const r = deps.pyramidView.groundRadiusUnder(id, deps.focusRig.focusCam.eyeWorld());
    return r === null ? null : r - deps.pyramidView.groundGlideM;
  }

  function eyeRadius(): number {
    const e = deps.focusRig.focusCam.eyeWorld();
    const c = deps.focusRig.focusBodies.centre(deps.focusRig.focusCam.state.bodyId) ?? { x: 0, y: 0, z: 0 };
    return Math.hypot(e.x - c.x, e.y - c.y, e.z - c.z);
  }

  function groundAt(bodyId: string, latDeg: number, lonDeg: number): number {
    if (carriesLastGround() || holds(bodyId, latDeg, lonDeg)) return focusGroundM;
    // The focus camera reads its anchor the moment a jump sets it, a frame
    // before `updateFocusGround` would see the move; that read is the acquire.
    const cam = deps.focusRig.focusCam.state;
    if (bodyId === cam.bodyId && latDeg === cam.latDeg && lonDeg === cam.lonDeg
      && bodyId === state.bodyId && bodyId === deps.pyramidView.tileBody) {
      acquire(bodyId, latDeg, lonDeg);
      return focusGroundM;
    }
    return 0;
  }

  return {
    GROUND_GLIDE_M_PER_S,
    updateFocusGround,
    get eyeRode() { return eyeRode; },
    groundAt,
    state: {
      get focusGroundM() { return focusGroundM; },
    },
  };
}
